/**
 * Servicio central Trámites Fiscales — consumido por Visor, Laboral y redirects legacy.
 * Solo navegación y URLs; las pantallas embebidas llaman las mismas APIs /laboral y /sat.
 */
import { setStoredWorkModule, WORK_MODULES } from "@/modules/workModuleCore";
import { TRAMITES_TAB_IDS, normalizeTramitesTabId } from "@/modules/tramites/manifest";
import { FISCAL_DOCS_OWNER } from "@/modules/sat/fiscalDocsOwnership";
import {
  DATOS_MAESTROS_CHECK_ID_PATH,
  DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH,
} from "@/modules/datosMaestros/datosMaestrosHub";

export const TRAMITES_BASE_PATH = "/tramites-fiscales";

/** Rutas propias del módulo Trámites (sin saltar a Laboral/REPSE). */
export const TRAMITES_VALIDACION_RFC_SAT_PATH = "/tramites/validacion-rfc-sat";
export const TRAMITES_INSCRIPCION_SAT_PATH = "/tramites/inscripcion-trabajadores-sat";
export const TRAMITES_INSCRIPCION_IMSS_PATH = "/tramites/inscripcion-trabajadores-imss";

/** Rutas legacy que redirigen al módulo Trámites (marcadores / flujos antiguos). */
export const TRAMITES_LEGACY_PATHS = new Set([
  "/validador-rfc-sat",
  "/validador-nss-imss",
  "/inscripcion-trabajadores-sat",
  "/inscripcion-trabajadores-imss",
  "/validacion-rfc-sat",
  "/asistente-validacion-sat",
]);

export const TRAMITES_MATERIALIDAD_PATH = "/tramites/materialidad";

/** Rutas del módulo Trámites (sidebar + hub). */
export const TRAMITES_MODULE_PATHS = new Set([
  TRAMITES_BASE_PATH,
  "/tramites/inicio",
  "/tramites/validacion-datos",
  "/tramites/validador-nss",
  TRAMITES_MATERIALIDAD_PATH,
  TRAMITES_VALIDACION_RFC_SAT_PATH,
  TRAMITES_INSCRIPCION_SAT_PATH,
  TRAMITES_INSCRIPCION_IMSS_PATH,
]);

export function buildTramitesUrl(tabId) {
  if (!tabId || tabId === TRAMITES_TAB_IDS.SAT) return FISCAL_DOCS_OWNER.route;
  return `${TRAMITES_BASE_PATH}?tab=${encodeURIComponent(tabId)}`;
}

/** Mapa ruta legacy → URL destino en Trámites o Datos Maestros (canónico). */
export const TRAMITES_LEGACY_REDIRECT_TARGETS = {
  "/validador-rfc-sat": DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH,
  "/validacion-rfc-sat": DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH,
  "/tramites/validacion-datos": DATOS_MAESTROS_CHECK_ID_PATH,
  "/tramites/validacion-rfc-sat": DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH,
  "/validador-nss-imss": "/tramites/validador-nss",
  "/inscripcion-trabajadores-sat": TRAMITES_INSCRIPCION_SAT_PATH,
  "/inscripcion-trabajadores-imss": TRAMITES_INSCRIPCION_IMSS_PATH,
  "/asistente-validacion-sat": TRAMITES_INSCRIPCION_SAT_PATH,
};

/**
 * Activa el módulo Trámites y navega (para enlaces desde Laboral / Visor).
 * @param {import('react-router-dom').NavigateFunction} navigate
 * @param {string} [tabId]
 */
export function openTramitesModule(navigate, tabId) {
  if (typeof navigate !== "function") return;
  setStoredWorkModule(WORK_MODULES.TRAMITES_FISCALES);
  const url = buildTramitesUrl(tabId);
  navigate(url, { replace: false });
}

export function isTramitesPath(pathname) {
  const path = String(pathname || "").split("?")[0];
  return TRAMITES_MODULE_PATHS.has(path) || path.startsWith(`${TRAMITES_BASE_PATH}/`);
}

/** Resalta ítem del sidebar según ruta dedicada o ?tab= en /tramites-fiscales. */
export function isTramitesTabNavActive(location, tabId) {
  const path = String(location?.pathname || "").split("?")[0];
  const dedicated = {
    [TRAMITES_TAB_IDS.VALIDACION_RFC]: DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH,
    [TRAMITES_TAB_IDS.INSCRIPCION_SAT]: TRAMITES_INSCRIPCION_SAT_PATH,
    [TRAMITES_TAB_IDS.INSCRIPCION_IMSS]: TRAMITES_INSCRIPCION_IMSS_PATH,
    [TRAMITES_TAB_IDS.VALIDADOR_NSS]: "/tramites/validador-nss",
  };
  const expectedPath = dedicated[tabId];
  if (expectedPath && path === expectedPath) return true;
  if (path !== TRAMITES_BASE_PATH) return false;
  const raw = normalizeTramitesTabId(
    new URLSearchParams(location?.search || "").get("tab") || TRAMITES_TAB_IDS.SAT,
  );
  const expected = normalizeTramitesTabId(tabId || TRAMITES_TAB_IDS.SAT);
  return raw === expected;
}
